import React, { useState, useEffect, useCallback } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Select, Breadcrumb, Button, Row, Col, Table, Tag } from 'antd';

import PermissionHide from 'v2/components/Shared/PermissionHide';
import TopPage from 'v2/components/Shared/TopPage';
import Truncate from 'v2/components/Shared/TruncateText';
import { useListConnectActions } from 'v2/hooks/useListConnectActions';
import { useListConnectOptions } from 'v2/hooks/useListConnectOptions';
import { useListTriggers } from 'v2/hooks/useListTriggers';
import { useLoadConnect } from 'v2/hooks/useLoadConnect';
import { connectService } from 'v2/services/connect';

import EditAction from '../components/EditAction';
import NewAction from '../components/NewAction';
import { useActionState } from '../contexts/ActionStateContext';
import { useConnectState } from '../contexts/ConnectStateContext';

const Actions: React.FC = () => {
  const { currentConnect, setCurrentConnect } = useConnectState();
  const { openNewActionModal, setCurrentAction, openEditActionModal } = useActionState();
  const [filterAction, setFilterAction] = useState('');
  const [filterTrigger, setFilterTrigger] = useState('');

  const history = useHistory();
  const { connectId } = useParams<{ connectId: string }>();

  const { actions, isLoading: isFetching } = useListConnectActions(Number(connectId));
  const { connectData, error: loadConnectError } = useLoadConnect(connectId ? Number(connectId) : undefined);
  const { triggers } = useListTriggers();
  const { options } = useListConnectOptions(currentConnect?.autoresponder_id);

  useEffect(() => {
    if (!connectId) {
      history.push('/producer/connect/myconnects/');
      return;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (connectData) {
      setCurrentConnect(connectData);
    }
  }, [connectData, setCurrentConnect]);

  useEffect(() => {
    if (loadConnectError) {
      toast.error('Erro ao carregar connect');
      history.push('/producer/connect/myconnects/');
    }
  }, [loadConnectError, history]);

  const openModal = useCallback(() => {
    openNewActionModal();
  }, [openNewActionModal]);

  const openModalEdit = useCallback(
    async (action: any) => {
      try {
        const result = await connectService.loadAction(currentConnect.id, action.id);
        const loadedAction = result?.data ?? result;
        setCurrentAction(loadedAction);
        openEditActionModal();
      } catch (error: any) {
        toast.error(error?.message ?? 'Erro ao carregar ação');
      }
    },
    [currentConnect, setCurrentAction, openEditActionModal]
  );

  const listActions = Array.isArray(actions) ? actions : [];

  const filteredOptions = listActions.filter((la: any) => {
    if (filterAction && filterAction !== '' && la.option_id != filterAction) {
      return false;
    }

    if (filterTrigger && filterTrigger !== '' && la.trigger_id != filterTrigger) {
      return false;
    }

    return true;
  });

  const triggerSelectOptions = [
    { value: '', label: 'Todos' },
    ...(triggers && Array.isArray(triggers)
      ? triggers.map((option: any) => ({ value: option.id, label: option.name }))
      : [])
  ];

  const actionSelectOptions = [
    { value: '', label: 'Todas' },
    ...(options && Array.isArray(options)
      ? options.map((option: any) => ({ value: option.id, label: option.title }))
      : [])
  ];

  return (
    <>
      <Row gutter={[16, 16]} className='mb-4'>
        <Col xs={24}>
          <Breadcrumb
            items={[
              { title: <Link to='/producer/connect/myconnects'>Connect</Link> },
              { title: currentConnect?.name },
              { title: 'Lista de ações' }
            ]}
          />
        </Col>
      </Row>
      <TopPage
        title='Lista de ações'
        action={
          <PermissionHide action={'myeduzz:connect:criar'}>
            <Button icon={<PlusOutlined />} type='primary' onClick={openModal}>
              Nova ação
            </Button>
          </PermissionHide>
        }
      />
      <Row gutter={[16, 16]} align='bottom'>
        <Col xs={24} md={12}>
          <div className='flex items-center gap-3 py-2'>
            <div
              className='flex h-8.75 min-w-20 bg-contain bg-center bg-no-repeat'
              style={{ backgroundImage: `url(${currentConnect?.image})` }}
            />
            <div>
              <div className='font-medium'>{currentConnect?.name}</div>
              <span className='text-sm text-gray-500'>{currentConnect?.description}</span>
              <div className='mt-1 text-sm'>
                <strong>{currentConnect?.parameters[0]?.label}</strong>
              </div>
              <Truncate count={50} final='...'>
                {currentConnect?.parameters[0]?.value}
              </Truncate>
            </div>
          </div>
        </Col>

        <Col xs={24} md={6}>
          <div>
            <label className='mb-1 block text-sm text-gray-600'>Gatilho</label>
            <Select
              value={filterTrigger}
              onChange={value => setFilterTrigger(value as string)}
              className='w-full'
              options={triggerSelectOptions}
            />
          </div>
        </Col>

        <Col xs={24} md={6}>
          <div>
            <label className='mb-1 block text-sm text-gray-600'>Ação</label>
            <Select
              value={filterAction}
              onChange={value => setFilterAction(value as string)}
              className='w-full'
              options={actionSelectOptions}
            />
          </div>
        </Col>

        <Col xs={24}>
          <Table
            columns={[
              {
                title: 'Status',
                dataIndex: 'active',
                key: 'status',
                render: (active: boolean) => (
                  <Tag color={active ? 'success' : 'error'}>{active ? 'Ativo' : 'Inativo'}</Tag>
                )
              },
              {
                title: 'Descrição',
                dataIndex: 'description',
                key: 'description'
              },
              {
                title: 'Produto',
                key: 'product',
                render: (_: any, record: any) => (
                  <>
                    <strong>{record.product_id}</strong>
                    <Truncate final='...' count={50}>
                      {record.product_title}
                    </Truncate>
                  </>
                )
              },
              {
                title: 'Gatilho',
                dataIndex: 'trigger',
                key: 'trigger'
              },
              {
                title: 'Ação',
                dataIndex: 'action',
                key: 'action'
              },
              {
                title: '',
                key: 'edit',
                width: 50,
                render: (_: any, record: any) => (
                  <PermissionHide action={'myeduzz:connect:editar'}>
                    <Button type='link' onClick={() => openModalEdit(record)}>
                      <EditOutlined />
                      Editar
                    </Button>
                  </PermissionHide>
                )
              }
            ]}
            dataSource={filteredOptions}
            rowKey={(record: any) => `connect-${record.id}`}
            loading={isFetching}
            pagination={false}
            locale={{ emptyText: 'Nenhuma ação encontrada' }}
          />
        </Col>
      </Row>
      <NewAction />
      <EditAction />
    </>
  );
};

export default Actions;
